// Treasury → Conversion pools (SM-467, made currency-explicit by SM-502).
//
// GREEN converts to RED and to GOLD, and a conversion pays the user out
// of a prefunded pool. So this page answers one operational question —
// "can the platform honour the next conversion, and for how long?" — and
// gives the actions that change the answer: fund, rebalance, configure
// the route, and price a test amount against it.
//
// The ordering constraint that makes it a rollout gate: the GOLD pool
// must be fundable HERE before GREEN→GOLD is switched on for users
// (R3c). That is why the page works while F3 is still off — funding
// precedes enablement, and a page that only worked after enablement
// would be a page that arrived too late.
//
// SM-502's correction. Every FIGURE on this page already named its
// currency; every route IDENTITY did not. A pool was "the GOLD pool"
// and a route was the wire string "GREEN → GOLD" — two codes the
// registry never saw, sitting above balances drawn in whatever mark and
// name an operator had configured. A conversion has two currencies and
// exactly one of them is the pool's, so the pair is now stated wherever
// a route is named, in configured marks and names, and it is filterable
// on both sides.
//
// Four things this page refuses to do:
//   - It never edits a balance. Every number comes from the ledger's
//     pool read and every change goes through the audited transfer.
//   - It never shows an unexecuted user quote as reserved liquidity.
//     `reserved` is ACTIVE ledger operations only (SM-421); a quote that
//     nobody executed has not reserved anything, and pretending it has
//     would make an operator fund against demand that does not exist.
//   - It never sums two currencies. A RED pool and a GOLD pool have no
//     common total, and there is no "total liquidity" figure anywhere
//     (G8).
//   - It never prints an amount without the currency it is in. That now
//     includes the CSV exports, whose amount columns are paired with
//     currency columns rather than headed with one currency's name.
//
// No mode filter, and that is a finding rather than an omission: a
// conversion route is a currency pair, not a book. GAME and CASH scope
// what a market is priced in — the trading surfaces filter on it — but
// SYSTEM_CONVERSION holds one balance per currency whichever book a
// user came from, and a mode control here would filter nothing.
import { useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";

import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { Select } from "@/components/ui/Select";
import { Skeleton } from "@/components/ui/Skeleton";
import { TechHint } from "@/components/ui/TechHint";
import { NotFoundSurface } from "@/components/ui/NotFoundSurface";
import { CurrencyAmount, CurrencyBadge, CurrencyMonogram } from "@/components/ui/CurrencyAmount";
import {
  PoolMovementDialog,
  type PoolMovementKind,
} from "@/components/treasury/PoolMovementDialog";
import { PoolSwitchDialog } from "@/components/treasury/PoolSwitchDialog";
import { RouteConfigDialog } from "@/components/treasury/RouteConfigDialog";
import { TestConversionDialog } from "@/components/treasury/TestConversionDialog";
import {
  ConversionPair,
  ConversionRouteLabel,
  parseRoutePair,
  routePairOf,
} from "@/components/currency/ConversionPair";
import {
  useCurrencyName,
  useCurrencySettings,
} from "@/components/currency/CurrencyPresentationProvider";
import { resolveCurrencyPresentation } from "@/currency";
import {
  currencyKeys,
  fetchConversionPools,
  fetchConversionRecon,
  fetchConversionRoutes,
  fetchConversionSwitches,
  fetchPoolMovements,
} from "@/lib/currency-queries";
import type {
  ConversionPoolStatus,
  ConversionReconRouteTotals,
  ConversionRouteConfig,
  ConversionSwitchChange,
  PoolMovementItem,
} from "@/lib/currency-types";
import { fetchSystemAccounts, keys } from "@/lib/queries";
import { fmtRelative } from "@/lib/format";
import { downloadCSV, toCSV } from "@/lib/csv";
import { isCurrencyCode, type CurrencyCode } from "@/lib/currency";
import { Perm } from "@/lib/perms";
import { useMyPerms } from "@/hooks/useMyPerms";
import { conversionPoolsEnabled } from "@/lib/featureFlags";
import { adminName, useAdminMap } from "@/hooks/useAdminNames";

interface PoolsSearch {
  source?: CurrencyCode;
  destination?: CurrencyCode;
}

export const Route = createFileRoute("/_app/treasury/pools")({
  // Both sides OPTIONAL, and absent means "every route" rather than a
  // default currency — unlike /treasury/accounts, where the picker
  // chooses WHICH of several same-named accounts the page is about. Here
  // the unfiltered page is the useful one: an operator arrives to see
  // whether anything is dry, and defaulting to one pair would hide the
  // pool that is.
  //
  // Declared on THIS route, not on the treasury layout: a layout's
  // validateSearch is inherited by every page beneath it, and Payouts,
  // System accounts and the Revenue group each own a different
  // `?currency=` (SM-494 follow-up).
  validateSearch: (search: Record<string, unknown>): PoolsSearch => ({
    ...(isCurrencyCode(search.source) ? { source: search.source } : {}),
    ...(isCurrencyCode(search.destination) ? { destination: search.destination } : {}),
  }),
  component: PoolsPage,
});

function PoolsPage() {
  // G1: gates this page's INITIATION only. No movement is in flight on a
  // page — a pending pool transfer lives in the ledger and shows up in
  // the movement history whether or not this route is built.
  if (!conversionPoolsEnabled()) return <NotFoundSurface />;
  return <PoolsView />;
}

// --- role + kind literals ------------------------------------------
//
// Hoisted so no bare string sits inside a JSX expression, where the
// i18n extractor would file "SOURCE_POOL" as a phrase to translate.
const ROLE_SOURCE_POOL = "SOURCE_POOL";
const KIND_CONVERSION = "CONVERSION";
const ALL_CURRENCIES = "";
const LEG_SOURCE = "source";

/**
 * The currency pair a pool row belongs to.
 *
 * A SOURCE_POOL is the DESTINATION half of its route — it holds what the
 * platform pays out — and its source comes from the route. The receipt
 * sink is the SOURCE half of every route at once: it holds the GREEN
 * users spend, and it has no single destination. Returning `undefined`
 * for that side rather than guessing one is what keeps a destination
 * filter from quietly claiming the sink belongs to whichever route
 * happens to be listed first.
 */
export function poolPair(
  pool: ConversionPoolStatus,
  routes: ConversionRouteConfig[],
): { source?: CurrencyCode; destination?: CurrencyCode } {
  const currency = isCurrencyCode(pool.currency) ? pool.currency : undefined;
  if (pool.role !== ROLE_SOURCE_POOL) return { source: currency };
  const config = routes.find((r) => r.route === pool.route);
  const pair = config ? routePairOf(config) : parseRoutePair(pool.route);
  return { source: pair?.source, destination: currency };
}

/**
 * Does a pool survive the filter?
 *
 * A side the pool does not have never matches a filter on that side.
 * The receipt sink has no destination, so asking for "everything paying
 * out in GOLD" excludes it — correctly: it pays out nothing.
 */
export function poolMatches(
  pool: ConversionPoolStatus,
  routes: ConversionRouteConfig[],
  filter: PoolsSearch,
): boolean {
  const pair = poolPair(pool, routes);
  if (filter.source && pair.source !== filter.source) return false;
  if (filter.destination && pair.destination !== filter.destination) return false;
  return true;
}

function PoolsView() {
  const perms = useMyPerms();
  const navigate = useNavigate();
  const search = Route.useSearch();
  // While permissions are still loading, show the actions: the server
  // enforces them regardless, and a button that appears late is worse
  // than one that is refused.
  const canManage = !perms.loaded || perms.isSuperAdmin || perms.has(Perm.ConversionPoolManage);
  const canConfigure = !perms.loaded || perms.isSuperAdmin || perms.has(Perm.ConversionConfig);

  const pools = useQuery({ queryKey: currencyKeys.pools(), queryFn: fetchConversionPools });
  const routes = useQuery({ queryKey: currencyKeys.routes(), queryFn: fetchConversionRoutes });
  const recon = useQuery({ queryKey: currencyKeys.recon(), queryFn: fetchConversionRecon });
  const accounts = useQuery({
    queryKey: keys.systemAccounts(),
    queryFn: fetchSystemAccounts,
  });

  const [dialog, setDialog] = useState<{ kind: PoolMovementKind; pool: ConversionPoolStatus } | null>(
    null,
  );
  const [configRoute, setConfigRoute] = useState<ConversionRouteConfig | null>(null);
  const [switchPool, setSwitchPool] = useState<ConversionPoolStatus | null>(null);
  // `undefined` is closed; a string (possibly empty) is open with that
  // route preselected. A plain `string | null` cannot express "open with
  // nothing preselected", which is what the toolbar button wants.
  const [testingRoute, setTestingRoute] = useState<string | undefined>(undefined);

  const routeList = useMemo(() => routes.data ?? [], [routes.data]);
  const allPools = useMemo(() => pools.data ?? [], [pools.data]);
  const routeFor = (pool: ConversionPoolStatus) =>
    routeList.find((r) => r.route === pool.route) ??
    routeList.find((r) => r.destination_currency === pool.currency);

  const visible = useMemo(
    () => allPools.filter((p) => poolMatches(p, routeList, search)),
    [allPools, routeList, search],
  );
  const sourcePools = visible.filter((p) => p.role === ROLE_SOURCE_POOL);
  const sinks = visible.filter((p) => p.role !== ROLE_SOURCE_POOL);
  const filtered = Boolean(search.source || search.destination);

  const setFilter = (patch: PoolsSearch) =>
    navigate({ to: ".", search: (prev) => ({ ...prev, ...patch }), replace: true });

  const reconRows = (recon.data ?? []).filter(
    (r) =>
      (!search.source || r.source_currency === search.source) &&
      (!search.destination || r.destination_currency === search.destination),
  );

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-2xl tracking-tight">Conversion pools</h1>
        <p className="mt-1 max-w-[85ch] text-sm text-muted">
          GREEN converts to RED and to GOLD, and each conversion pays out of the
          destination currency's prefunded pool. Fund a pool before its route is
          enabled for users, and rebalance surplus back to treasury. Every figure
          on this page is single-currency — pools are never added together.
        </p>
      </header>

      <PairFilter
        routes={routeList}
        pools={allPools}
        value={search}
        onChange={setFilter}
        onTest={() => setTestingRoute("")}
        canTest={routeList.length > 0}
      />

      {/*
        `isPending`, not `isLoading`. They differ in exactly the state
        this page must not get wrong: a read that has failed and had its
        retry PAUSED (no connection) is pending but not fetching, so
        `isLoading` is false while `isError` is still false too — and the
        render fell through to the empty state below, which does not say
        "unknown", it says "the ledger reports no source pool". An
        operator would read a dead read as a platform with no pools.
        Every empty state on this page asserts a fact, so each one is
        reachable only from a read that actually succeeded.
      */}
      {pools.isPending ? (
        <Skeleton className="h-48" />
      ) : pools.isError ? (
        <Card>
          <CardBody>
            <ReadFailure
              message="Pool liquidity could not be read. Do not fund against a stale number — retry before acting."
              query={pools}
              size="sm"
            />
          </CardBody>
        </Card>
      ) : sourcePools.length === 0 ? (
        // Two different facts, and an operator acts differently on each:
        // no pool exists at all, versus none matches the pair they asked
        // for. Saying the first when the second is true reads as an
        // outage.
        filtered ? (
          <EmptyState
            title="No pool for this currency pair"
            description="No conversion pool matches both sides of the filter. Clear it to see every pool the ledger reports."
          />
        ) : (
          <EmptyState
            title="No conversion pools"
            description="The ledger reports no source pool. Conversion routes cannot pay out until one exists."
          />
        )
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {sourcePools.map((pool) => (
            <PoolCard
              key={`${pool.currency}-${pool.role}`}
              pool={pool}
              route={routeFor(pool)}
              canManage={canManage}
              canConfigure={canConfigure}
              onFund={() => setDialog({ kind: "fund", pool })}
              onRebalance={() => setDialog({ kind: "rebalance", pool })}
              onConfigure={(r) => setConfigRoute(r)}
              onTest={(r) => setTestingRoute(r)}
              onSwitch={() => setSwitchPool(pool)}
            />
          ))}
        </div>
      )}

      {sinks.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-2xs font-semibold uppercase tracking-wide text-t-3">
            Receipt account
          </h2>
          <p className="max-w-[85ch] text-2xs text-t-3">
            Where the GREEN a user spends on a conversion lands, gross of fees.
            It is not a payout source and is not funded from here.
          </p>
          <div className="grid gap-4 lg:grid-cols-2">
            {sinks.map((pool) => (
              <PoolCard key={`${pool.currency}-${pool.role}`} pool={pool} readOnly />
            ))}
          </div>
        </section>
      )}

      <ReconSection rows={reconRows} query={recon} filtered={filtered} />

      <SwitchHistory />

      <MovementHistory pools={visible} routes={routeList} />

      {dialog && (
        <PoolMovementDialog
          kind={dialog.kind}
          pool={dialog.pool}
          route={routeFor(dialog.pool)}
          accounts={accounts.data ?? []}
          onClose={() => setDialog(null)}
        />
      )}
      {configRoute && (
        // Keyed by route id so switching routes remounts rather than
        // leaving a draft built from the previous route's stored values
        // and the previous route's currencies (AC7).
        <RouteConfigDialog
          key={configRoute.route}
          route={configRoute}
          pool={allPools.find(
            (p) => p.role === ROLE_SOURCE_POOL && p.currency === configRoute.destination_currency,
          )}
          onClose={() => setConfigRoute(null)}
        />
      )}
      {switchPool && (
        // Keyed by currency AND by the switch's current position: the
        // dialog's direction and its preview are both built from that
        // position, and a stale copy would offer "Stop" on a pool a
        // colleague just stopped.
        <PoolSwitchDialog
          key={`${switchPool.currency}-${switchPool.enabled}`}
          pool={
            // Always the FRESH row, not the one captured at click time —
            // this page refetches, and a switch is exactly the kind of
            // state another operator may have moved in between.
            allPools.find(
              (p) => p.role === ROLE_SOURCE_POOL && p.currency === switchPool.currency,
            ) ?? switchPool
          }
          route={routeFor(switchPool)}
          onClose={() => setSwitchPool(null)}
        />
      )}
      {testingRoute !== undefined && (
        <TestConversionDialog
          routes={routeList}
          pools={allPools}
          initialRoute={testingRoute || undefined}
          onClose={() => setTestingRoute(undefined)}
        />
      )}
    </div>
  );
}

// --- a failed read, and the way back from it ----------------------

/**
 * The retry an operator gets when one of this page's reads fails.
 *
 * Every read here refuses to render a stale number rather than showing
 * one that might be wrong, which is right — but before this the only
 * way forward was a full page reload, and a reload re-runs every read
 * on the page to recover one of them. `refetch` re-runs exactly the
 * failed query and leaves the sections that DID load alone.
 *
 * Disabled while the refetch is in flight so a second click cannot
 * queue another round-trip behind the first, and it says "Retrying…"
 * so a slow failure still looks like progress.
 */
export function ReadFailure({
  message,
  query,
  // The pool cards are this page's primary surface and their failure
  // keeps the weight it had; the subordinate sections stay quiet.
  size = "2xs",
}: {
  message: string;
  query: { refetch: () => void; isFetching: boolean };
  size?: "sm" | "2xs";
}) {
  return (
    <div className="space-y-2">
      <p className={`${size === "sm" ? "text-sm" : "text-2xs"} text-no`}>
        {message}
      </p>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => query.refetch()}
        disabled={query.isFetching}
      >
        {query.isFetching ? "Retrying…" : "Retry"}
      </Button>
    </div>
  );
}

// --- the pair filter -----------------------------------------------

/**
 * Source and destination pickers, built from the routes that EXIST.
 *
 * Offering every registry currency on both sides would offer pairs the
 * platform has no route for — GOLD → RED is not a thing, and a filter
 * that can select it teaches an operator that it might be. So the
 * options come from the route list, and fall back to the currencies the
 * pool rows name when the route read is unavailable.
 */
function PairFilter({
  routes,
  pools,
  value,
  onChange,
  onTest,
  canTest,
}: {
  routes: ConversionRouteConfig[];
  pools: ConversionPoolStatus[];
  value: PoolsSearch;
  onChange: (patch: PoolsSearch) => void;
  onTest: () => void;
  canTest: boolean;
}) {
  const settings = useCurrencySettings();
  const sources = useMemo(() => {
    const codes = routes.map((r) => routePairOf(r)?.source).filter(isCurrencyCode);
    if (codes.length) return [...new Set(codes)];
    return [...new Set(pools.filter((p) => p.role !== ROLE_SOURCE_POOL).map((p) => p.currency))].filter(
      isCurrencyCode,
    );
  }, [routes, pools]);
  const destinations = useMemo(() => {
    const codes = routes.map((r) => routePairOf(r)?.destination).filter(isCurrencyCode);
    if (codes.length) return [...new Set(codes)];
    return [...new Set(pools.filter((p) => p.role === ROLE_SOURCE_POOL).map((p) => p.currency))].filter(
      isCurrencyCode,
    );
  }, [routes, pools]);

  const option = (code: CurrencyCode) => ({
    value: code,
    // Name AND code: the name is what the console shows everywhere else,
    // the code is what a support ticket or an API call says.
    label: `${resolveCurrencyPresentation(code, settings).displayName} (${code})`,
    icon: <CurrencyMonogram currency={code} />,
  });

  const active = Boolean(value.source || value.destination);

  return (
    <div className="flex flex-wrap items-end gap-3">
      <Select
        className="w-56"
        label="Converted from"
        value={value.source ?? ALL_CURRENCIES}
        placeholder="Any source currency"
        options={sources.map(option)}
        onChange={(e) =>
          onChange({ source: isCurrencyCode(e.target.value) ? e.target.value : undefined })
        }
      />
      <Select
        className="w-56"
        label="Converted to"
        value={value.destination ?? ALL_CURRENCIES}
        placeholder="Any destination currency"
        options={destinations.map(option)}
        onChange={(e) =>
          onChange({ destination: isCurrencyCode(e.target.value) ? e.target.value : undefined })
        }
      />
      {active && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onChange({ source: undefined, destination: undefined })}
        >
          Clear filter
        </Button>
      )}
      <div className="ml-auto">
        <Button variant="ghost" size="sm" disabled={!canTest} onClick={onTest}>
          Test a conversion
        </Button>
      </div>
    </div>
  );
}

// --- one pool ------------------------------------------------------

function PoolCard({
  pool,
  route,
  canManage,
  canConfigure,
  readOnly,
  onFund,
  onRebalance,
  onConfigure,
  onTest,
  onSwitch,
}: {
  pool: ConversionPoolStatus;
  route?: ConversionRouteConfig;
  canManage?: boolean;
  canConfigure?: boolean;
  readOnly?: boolean;
  onFund?: () => void;
  onRebalance?: () => void;
  onConfigure?: (route: ConversionRouteConfig) => void;
  onTest?: (route: string) => void;
  onSwitch?: () => void;
}) {
  const currency = pool.currency;
  const state = poolState(pool);
  const name = useCurrencyName(currency);
  const pair = route ? routePairOf(route) : parseRoutePair(pool.route);

  return (
    <Card>
      <CardHeader>
        <div className="flex min-w-0 items-center gap-2">
          <CurrencyBadge currency={currency} />
          {/* The pool's own name, from the registry. "GOLD pool" was the
              wire code on a card whose every figure below wears the
              configured mark. */}
          <CardTitle>{`${name} pool`}</CardTitle>
          <Badge tone={state.tone}>{state.label}</Badge>
        </div>
        {pool.route && (
          <TechHint tech={pool.route}>
            <ConversionRouteLabel route={pool.route} className="shrink-0 text-2xs" />
          </TechHint>
        )}
      </CardHeader>
      <CardBody className="space-y-4">
        <dl className="grid grid-cols-3 gap-3">
          <Figure label="Total" currency={currency} value={pool.total} />
          <Figure label="Available" currency={currency} value={pool.available} />
          <Figure
            label="Reserved"
            currency={currency}
            value={pool.reserved}
            note={
              pool.role === ROLE_SOURCE_POOL
                ? "Active ledger operations only — never an unexecuted quote."
                : undefined
            }
          />
        </dl>

        <dl className="space-y-1 border-t border-line-1 pt-3 text-2xs">
          {/* The pair, spelled out on the card rather than only in the
              route chip: this is the line that says which currency a
              user SPENDS to draw the balance above down. */}
          {pair && (
            <Line
              label={pool.role === ROLE_SOURCE_POOL ? "Pays out for" : "Receives from"}
              value={<ConversionPair source={pair.source} destination={pair.destination} />}
            />
          )}
          {pool.role === ROLE_SOURCE_POOL && (
            <Line
              label="Minimum threshold"
              value={
                pool.low_threshold ? (
                  <CurrencyAmount currency={currency} value={pool.low_threshold} />
                ) : (
                  "Not set"
                )
              }
            />
          )}
          {pool.role === ROLE_SOURCE_POOL && route?.active_rate_version && pair && (
            <Line
              label="Active rate"
              value={`${route.active_rate_version.rate_num} / ${route.active_rate_version.rate_den} ${pair.destination} per ${pair.source}`}
            />
          )}
          {pool.role === ROLE_SOURCE_POOL && (
            <Line label="Route status" value={routeStatusText(pool, route)} />
          )}
          {/* The pool switch, as its own line with its own attribution
              (SM-524). Folded into "route status" it would read as the
              route's switch, which is the confusion this ticket
              exists to end. */}
          {pool.role === ROLE_SOURCE_POOL && (
            <Line label="Pool switch" value={poolSwitchText(pool)} />
          )}
          <Line label="Pool account" value={<TechHint tech={pool.wallet_id}>{pool.account_code}</TechHint>} />
        </dl>

        {state.explain && (
          <p
            className={
              state.tone === "danger"
                ? "rounded-md border border-no/40 bg-no/5 px-3 py-2 text-2xs text-no"
                : "rounded-md border border-warn/40 bg-warn/5 px-3 py-2 text-2xs text-warn"
            }
          >
            {state.explain}
          </p>
        )}

        {!readOnly && (
          <div className="flex flex-wrap gap-2">
            {/* The emergency stop comes FIRST and is styled as the
                consequential action it is. An operator reaching this
                card during an incident is not looking for "Fund". */}
            {onSwitch && (
              <Button
                variant={pool.enabled === false ? "primary" : "danger"}
                size="sm"
                disabled={!canManage}
                onClick={onSwitch}
              >
                {pool.enabled === false ? "Resume payouts" : "Stop payouts"}
              </Button>
            )}
            <Button variant="ghost" size="sm" disabled={!canManage} onClick={onFund}>
              Fund
            </Button>
            <Button variant="ghost" size="sm" disabled={!canManage} onClick={onRebalance}>
              Rebalance
            </Button>
            {/* Route config (SM-423): a different write, a different
                permission — switch, fee, limits and rate versions rather
                than money movement. Absent route read = nothing to edit. */}
            {route && onConfigure && (
              <Button
                variant="ghost"
                size="sm"
                disabled={!canConfigure}
                onClick={() => onConfigure(route)}
              >
                Configure route
              </Button>
            )}
            {/* Reading, not writing, so it is offered to anyone who can
                see the page: pricing a test amount moves nothing and
                consumes no user's daily headroom. */}
            {route && onTest && (
              <Button variant="ghost" size="sm" onClick={() => onTest(route.route)}>
                Test conversion
              </Button>
            )}
            {!canManage && (
              <span className="self-center text-2xs text-t-4">
                Needs the conversion pool-manage permission.
              </span>
            )}
          </div>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * The pool's headline state.
 *
 * Exhausted outranks low, and both outrank "route disabled": an operator
 * looking at an empty pool needs to know it is empty first and that its
 * route happens to be off second — the reverse ordering hides the
 * expensive fact behind the cheap one.
 */
export function poolState(pool: ConversionPoolStatus): {
  label: string;
  tone: "success" | "warning" | "danger" | "muted";
  explain?: string;
} {
  // The receipt sink is not a payout source: it has no threshold, it
  // cannot be "exhausted", and calling it "funded" would invite an
  // operator to fund it. It only ever receives.
  if (pool.role !== ROLE_SOURCE_POOL) {
    return { label: "Receiving", tone: "muted" };
  }
  // A STOPPED pool outranks even an exhausted one (SM-524), and for the
  // same reason exhausted outranks low: it is the fact that explains
  // every refusal a user is getting, and it is the one an operator can
  // undo with one click. It also matches the service's gate order — the
  // pool switch is checked before liquidity — so the card and the error
  // a user sees name the same cause.
  if (pool.enabled === false) {
    return {
      label: "Stopped",
      tone: "danger",
      explain:
        "An operator stopped payouts from this pool. Conversions into this currency are refused until it is resumed; the balance is untouched.",
    };
  }
  if (pool.exhausted) {
    return {
      label: "Exhausted",
      tone: "danger",
      explain:
        "The pool cannot pay out. Conversions into this currency fail until it is funded.",
    };
  }
  if (pool.low) {
    return {
      label: "Low",
      tone: "warning",
      explain:
        "Available liquidity is at or below the minimum threshold. Fund it before the route runs dry.",
    };
  }
  if (pool.funded === false) {
    return {
      label: "Unfunded",
      tone: "warning",
      explain: "The pool has never been funded. Its route cannot pay out yet.",
    };
  }
  return { label: "Funded", tone: "success" };
}

/**
 * The pool switch line: its position, and who put it there.
 *
 * A stopped pool with no attribution is a mystery an operator has to
 * escalate; with the actor, the time and the reason it is a decision
 * they can either trust or reverse. The reason is the LAST one recorded,
 * which for a live pool is nothing at all — a resume clears it, because
 * a live pool carrying "drained during INC-42" reads as still broken.
 */
export function poolSwitchText(pool: ConversionPoolStatus): string {
  const state = pool.enabled === false ? "Stopped" : "Live";
  const parts = [state];
  if (pool.switch_updated_at) parts.push(fmtRelative(pool.switch_updated_at));
  if (pool.enabled === false && pool.disabled_reason) parts.push(pool.disabled_reason);
  return parts.join(" · ");
}

export function routeStatusText(
  pool: ConversionPoolStatus,
  route?: { enabled: boolean },
): string {
  // THREE facts now, and an operator needs all of them: the route's
  // stored switch, the pool's master switch, and the effective verdict.
  // Collapsing the two switches into one word is what made "switch
  // enabled · currently not paying out" unexplainable.
  const stored = route ? (route.enabled ? "enabled" : "disabled") : "not reported";
  const master = pool.enabled === false ? "pool stopped" : "pool live";
  const effective = pool.route_available ? "paying out" : "not paying out";
  return `Route ${stored} · ${master} · currently ${effective}`;
}

function Figure({
  label,
  currency,
  value,
  note,
}: {
  label: string;
  currency: string;
  value: string;
  note?: string;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-2xs text-t-4">{label}</dt>
      <dd className="truncate text-sm text-t-1">
        <CurrencyAmount currency={currency} value={value} />
      </dd>
      {note && <p className="mt-0.5 text-2xs leading-tight text-t-4">{note}</p>}
    </div>
  );
}

function Line({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="shrink-0 text-t-4">{label}</dt>
      <dd className="min-w-0 truncate text-right text-t-2">{value}</dd>
    </div>
  );
}

// --- reconciliation ------------------------------------------------

/**
 * Recon rows as CSV. Exported for the test that pins the pairing rule:
 * every amount column is immediately preceded by the currency column it
 * is denominated in, so no reader of the file can add two of them
 * together by accident (G8).
 */
export const RECON_CSV_HEADERS = [
  "route",
  "source_currency",
  "source_received",
  "source_fees_retained",
  "destination_currency",
  "destination_paid",
  "conversions",
] as const;

export function reconCSV(rows: ConversionReconRouteTotals[]): string {
  return toCSV(
    rows.map((r) => ({
      route: r.route,
      source_currency: r.source_currency,
      source_received: r.green_received,
      source_fees_retained: r.fees_retained,
      destination_currency: r.destination_currency,
      destination_paid: r.destination_paid,
      conversions: r.conversion_count,
    })),
    [...RECON_CSV_HEADERS],
  );
}

function ReconSection({
  rows,
  query,
  filtered,
}: {
  rows: ConversionReconRouteTotals[];
  query: ReturnType<typeof useQuery<Awaited<ReturnType<typeof fetchConversionRecon>>>>;
  filtered: boolean;
}) {
  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="text-2xs font-semibold uppercase tracking-wide text-t-3">
          Reconciliation
        </h2>
        {rows.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => downloadCSV("conversion-reconciliation.csv", reconCSV(rows))}
          >
            Export CSV
          </Button>
        )}
      </div>
      {query.isPending ? (
        <Skeleton className="h-24" />
      ) : query.isError ? (
        <div className="rounded-md border border-no/40 bg-no/5 px-3 py-2">
          <ReadFailure
            message="Reconciliation totals could not be read. Treat pool figures as unverified until this comes back."
            query={query}
          />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-2xs text-t-3">
          {filtered
            ? "No settled conversions on this currency pair."
            : "No conversions have settled yet."}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-line-1">
          <table className="w-full min-w-[46rem] text-xs">
            <thead>
              <tr className="border-b border-line-1 text-2xs uppercase tracking-wide text-t-4">
                <th className="px-3 py-2 text-left">Route</th>
                {/* Headed by ROLE, not by currency name. These columns
                    said "GREEN received" and "GREEN fees kept" for every
                    row, which was true only because every route happens
                    to convert out of GREEN today — a header that a
                    fourth currency would silently make wrong. Each cell
                    now names its own currency instead. */}
                <th className="px-3 py-2 text-right">Source received</th>
                <th className="px-3 py-2 text-right">Source fees kept</th>
                <th className="px-3 py-2 text-right">Destination paid</th>
                <th className="px-3 py-2 text-right">Conversions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.route} className="border-b border-line-1 last:border-0">
                  <td className="px-3 py-2">
                    <TechHint tech={r.route}>
                      <ConversionRouteLabel route={r.route} />
                    </TechHint>
                  </td>
                  {/* Each column names its own currency; the source
                      columns and the destination column are different
                      units and are never added (G8). */}
                  <td className="px-3 py-2 text-right">
                    <CurrencyAmount currency={r.source_currency} value={r.green_received} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <CurrencyAmount currency={r.source_currency} value={r.fees_retained} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <CurrencyAmount currency={r.destination_currency} value={r.destination_paid} />
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.conversion_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// --- switch history ------------------------------------------------

const SWITCH_PAGE = 10;
const SCOPE_POOL = "POOL";

/**
 * Who stopped or resumed what, and why (SM-524 AC12).
 *
 * Separate from the movement history below on purpose: those rows are
 * money, these are decisions. An operator asking "why is GOLD not
 * paying out?" is looking for a decision, and searching for it among
 * funding transfers is how that question goes unanswered.
 *
 * Unfiltered by the pair pickers: a switch flip that stopped a pool is
 * the reason its routes are dark, so hiding it behind a filter for a
 * pair whose card is showing "Stopped" would hide the explanation from
 * the person looking straight at the symptom.
 */
function SwitchHistory() {
  const changes = useQuery({
    queryKey: currencyKeys.switches(SWITCH_PAGE),
    queryFn: () => fetchConversionSwitches(SWITCH_PAGE),
  });
  const admins = useAdminMap();

  return (
    <section className="space-y-2">
      <h2 className="text-2xs font-semibold uppercase tracking-wide text-t-3">
        Switch history
      </h2>
      <p className="max-w-[85ch] text-2xs text-t-3">
        Every enable and disable on a pool or a route, newest first. Append-only:
        a resume adds a row, it never erases the stop.
      </p>
      {changes.isPending ? (
        <Skeleton className="h-24" />
      ) : changes.isError ? (
        <ReadFailure
          message={'Switch history could not be read. A pool showing "Stopped" here has no explanation attached until it comes back.'}
          query={changes}
        />
      ) : (changes.data ?? []).length === 0 ? (
        <p className="text-2xs text-t-3">No switch has been flipped yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-line-1">
          <table className="w-full min-w-[48rem] text-xs">
            <thead>
              <tr className="border-b border-line-1 text-2xs uppercase tracking-wide text-t-4">
                <th className="px-3 py-2 text-left">When</th>
                <th className="px-3 py-2 text-left">Switch</th>
                <th className="px-3 py-2 text-left">Change</th>
                <th className="px-3 py-2 text-left">Affects</th>
                <th className="px-3 py-2 text-left">Actor</th>
                <th className="px-3 py-2 text-left">Reason</th>
                <th className="px-3 py-2 text-left">Correlation</th>
              </tr>
            </thead>
            <tbody>
              {(changes.data ?? []).map((c) => (
                <tr key={c.change_id} className="border-b border-line-1 last:border-0">
                  <td className="px-3 py-2 whitespace-nowrap">
                    <TechHint tech={c.changed_at}>{fmtRelative(c.changed_at)}</TechHint>
                  </td>
                  <td className="px-3 py-2">{switchLabel(c)}</td>
                  <td className="px-3 py-2">
                    <Badge tone={c.new_enabled ? "success" : "danger"}>
                      {c.new_enabled ? "Enabled" : "Disabled"}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 text-t-2">
                    {c.route ? <ConversionRouteLabel route={c.route} /> : "—"}
                  </td>
                  <td className="px-3 py-2 text-t-2">
                    {c.changed_by ? adminName(admins, c.changed_by) : "—"}
                  </td>
                  <td className="px-3 py-2 text-t-2">{c.reason || "—"}</td>
                  <td className="px-3 py-2">
                    <span className="font-mono text-2xs">{c.correlation_id || "—"}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * Which switch a row is about, in words an operator uses.
 *
 * "POOL / RED" is the wire shape; "RED pool" is the thing on the card
 * above. Naming the scope is what stops a pool row and a route row
 * reading as the same event.
 */
export function switchLabel(change: ConversionSwitchChange): string {
  if (change.scope === SCOPE_POOL) return `${change.target} pool`;
  return `${change.target} route`;
}

// --- movement history ----------------------------------------------

const MOVEMENT_PAGE = 25;

/**
 * Which half of a conversion a movement row is.
 *
 * A fund or a rebalance is a same-currency transfer and has no halves.
 * A CONVERSION leg is one side of a two-currency event, and the pool it
 * landed on is what says which side: money arriving in the GREEN receipt
 * sink is what a user SPENT, money leaving a payout pool is what they
 * RECEIVED. Naming that is the difference between a history that records
 * a conversion and one that records an unexplained transfer.
 *
 * The counterpart AMOUNT is deliberately absent: this endpoint returns
 * one pool's legs, the other leg lives on another pool, and inventing it
 * from the rate would print a number the ledger never wrote.
 */
export function movementLeg(
  movement: PoolMovementItem,
  pool?: ConversionPoolStatus,
): "source" | "destination" | undefined {
  if (movement.kind !== KIND_CONVERSION || !pool) return undefined;
  return pool.role === ROLE_SOURCE_POOL ? "destination" : "source";
}

export const MOVEMENT_CSV_HEADERS = [
  "created_at",
  "kind",
  "leg",
  "route",
  "amount",
  "currency",
  "from_account",
  "to_account",
  "actor_admin_id",
  "operation_id",
  "transaction_id",
  "reason",
] as const;

export function movementsCSV(
  movements: PoolMovementItem[],
  pool: ConversionPoolStatus | undefined,
): string {
  return toCSV(
    movements.map((m) => ({
      created_at: m.created_at,
      kind: m.kind,
      leg: movementLeg(m, pool) ?? "",
      route: pool?.route ?? "",
      // Amount and currency are adjacent columns, always both present.
      // An export whose amount column is headed by one currency is the
      // spreadsheet version of summing two of them.
      amount: m.amount,
      currency: m.currency,
      from_account: m.from_account_code ?? "",
      to_account: m.to_account_code ?? "",
      actor_admin_id: m.actor_admin_id ?? "",
      operation_id: m.operation_id,
      transaction_id: m.transaction_id,
      reason: m.reason ?? "",
    })),
    [...MOVEMENT_CSV_HEADERS],
  );
}

function MovementHistory({
  pools,
  routes,
}: {
  pools: ConversionPoolStatus[];
  routes: ConversionRouteConfig[];
}) {
  const currencies = [...new Set(pools.map((p) => p.currency))];
  const [picked, setPicked] = useState<string>("");
  // The pill selection is dropped as soon as the pair filter stops
  // offering it — otherwise narrowing the filter leaves the table
  // showing a pool the page above no longer lists (AC7).
  const active = (picked && currencies.includes(picked) ? picked : currencies[0]) ?? "";
  const pool = pools.find((p) => p.currency === active);
  const pair = pool ? poolPair(pool, routes) : undefined;
  const activeName = useCurrencyName(active);

  const movements = useQuery({
    queryKey: currencyKeys.poolMovements(active, 0, MOVEMENT_PAGE),
    queryFn: () => fetchPoolMovements(active, { limit: MOVEMENT_PAGE, offset: 0 }),
    enabled: !!active,
  });
  const admins = useAdminMap();
  const items = movements.data?.items ?? [];

  if (!active) return null;

  return (
    <section className="space-y-2">
      <div className="flex flex-wrap items-baseline gap-3">
        <h2 className="text-2xs font-semibold uppercase tracking-wide text-t-3">
          Movement history
        </h2>
        <div className="flex gap-1">
          {currencies.map((c) => (
            <CurrencyPill key={c} currency={c} active={c === active} onSelect={setPicked} />
          ))}
        </div>
        {pair?.source && pair?.destination && (
          <ConversionPair source={pair.source} destination={pair.destination} className="text-2xs" />
        )}
        {items.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto"
            onClick={() =>
              downloadCSV(`conversion-pool-${active.toLowerCase()}-movements.csv`, movementsCSV(items, pool))
            }
          >
            Export CSV
          </Button>
        )}
      </div>

      {movements.isPending ? (
        <Skeleton className="h-32" />
      ) : movements.isError ? (
        <ReadFailure message="Movement history could not be read." query={movements} />
      ) : items.length === 0 ? (
        <p className="text-2xs text-t-3">{`No movements on the ${activeName} pool yet.`}</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-line-1">
          <table className="w-full min-w-[58rem] text-xs">
            <thead>
              <tr className="border-b border-line-1 text-2xs uppercase tracking-wide text-t-4">
                <th className="px-3 py-2 text-left">When</th>
                <th className="px-3 py-2 text-left">Kind</th>
                <th className="px-3 py-2 text-left">Leg</th>
                <th className="px-3 py-2 text-right">Amount</th>
                <th className="px-3 py-2 text-left">From → to</th>
                <th className="px-3 py-2 text-left">Actor</th>
                <th className="px-3 py-2 text-left">Reference</th>
                <th className="px-3 py-2 text-left">Reason</th>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => (
                <tr key={m.transaction_id} className="border-b border-line-1 last:border-0">
                  <td className="px-3 py-2 whitespace-nowrap">
                    <TechHint tech={m.created_at}>{fmtRelative(m.created_at)}</TechHint>
                  </td>
                  <td className="px-3 py-2">{m.kind}</td>
                  <td className="px-3 py-2 text-t-2">
                    <MovementLegCell movement={m} pool={pool} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <CurrencyAmount currency={m.currency} value={m.amount} />
                  </td>
                  <td className="px-3 py-2 text-t-2">
                    {(m.from_account_code || "—") + " → " + (m.to_account_code || "—")}
                  </td>
                  <td className="px-3 py-2 text-t-2">
                    {/* A CONVERSION leg has no admin: a user made it
                        happen. Saying "user conversion" is the honest
                        answer; a blank cell reads as missing data. */}
                    {m.actor_admin_id ? adminName(admins, m.actor_admin_id) : "User conversion"}
                  </td>
                  <td className="px-3 py-2">
                    <TechHint tech={m.transaction_id}>
                      <span className="font-mono text-2xs">{m.operation_id || "—"}</span>
                    </TechHint>
                  </td>
                  <td className="px-3 py-2 text-t-2">{m.reason || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-2xs text-t-4">
        Rows are keyed by their idempotency reference: a replayed or
        double-submitted movement appears once, not twice. Each conversion leg
        records the amount and currency that moved on this pool; its
        counterpart leg is on the other side's pool.
      </p>
    </section>
  );
}

/** One currency's pill in the movement-history selector. */
function CurrencyPill({
  currency,
  active,
  onSelect,
}: {
  currency: string;
  active: boolean;
  onSelect: (currency: string) => void;
}) {
  const name = useCurrencyName(currency);
  return (
    <button
      type="button"
      onClick={() => onSelect(currency)}
      title={name}
      className={
        active
          ? "rounded-full border border-neon/40 bg-neon/10 px-2 py-0.5 text-2xs font-semibold text-neon"
          : "rounded-full border border-line-1 px-2 py-0.5 text-2xs text-t-3 hover:text-t-1"
      }
      aria-pressed={active}
      aria-label={name}
    >
      {currency}
    </button>
  );
}

function MovementLegCell({
  movement,
  pool,
}: {
  movement: PoolMovementItem;
  pool?: ConversionPoolStatus;
}) {
  const leg = movementLeg(movement, pool);
  const name = useCurrencyName(movement.currency);
  if (!leg) return <span className="text-t-4">—</span>;
  const spent = leg === LEG_SOURCE;
  return <span>{spent ? `Spent by the user (${name})` : `Paid to the user (${name})`}</span>;
}
